import { joiResolver } from '@hookform/resolvers/joi';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@src/components/atoms/inputs/button';
import { ContentWithLink } from '@src/components/common/formatting/content-with-link';
import Seo from '@src/components/common/seo/seo';
import { InputController } from '@src/components/input-controllers/input-controller';
import { Form } from '@src/components/molecules/form';
import { schemaMessages } from '@src/constants/schema-messages';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { useUserAgent } from '@src/contexts/user-agent-context/use-user-agent';
import { AuthLayout } from '@src/features/auth/components/auth-layout';
import { PasswordRules } from '@src/features/auth/components/password-rules';
import { useGuestConsents } from '@src/features/user-consent/hooks';
import { useErrorMessages } from '@src/hooks/use-error-messages';
import { usePostMessageToReactNativeWebView } from '@src/hooks/use-post-message';

import { FirstPartyConsent } from './components/first-party-consent';
import { GeneralTermsConsent } from './components/general-terms-consent';
import styles from './sign-up-form.module.css';
import { signUpSchema } from './sign-up-form.schema';
import { SignUpFormData, SignUpFormProps } from './sign-up-form.types';

const SignUpForm = ({ isLoading, onSignUp, email: defaultEmail, fullName: defaultFullName }: SignUpFormProps) => {
  const { isAppWebView } = useUserAgent();
  const { postMessage } = usePostMessageToReactNativeWebView();
  const content = useCmsContent();

  const form = useForm<SignUpFormData>({
    defaultValues: {
      email: defaultEmail,
      fullName: defaultFullName,
      consents: {
        marketing: true,
      },
    },
    resolver: joiResolver(signUpSchema, {
      messages: schemaMessages,
      abortEarly: false,
    }),
    criteriaMode: 'all',
    mode: 'all',
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = form;

  const errorMessages = useErrorMessages<SignUpFormData>(errors);
  const { email, password } = watch();

  const handleLoginClick = () => {
    if (isAppWebView) {
      postMessage({
        type: 'goToLogin',
      });
    }
  };

  const { isLoading: areGuestConsentsLoading, data: guestConsentsResponse } = useGuestConsents(email);

  return (
    <Form onSubmit={handleSubmit(onSignUp)}>
      <Seo title={content.signUpFormHeading} />
      <AuthLayout.Header size="small">{content.signUpFormHeading}</AuthLayout.Header>

      <InputController
        name="email"
        type="email"
        autoComplete="email"
        error={errorMessages.email}
        label={content.labelEmail}
        control={control}
      />

      <InputController
        error={errorMessages.fullName}
        label={content.labelFullName}
        control={control}
        name="fullName"
        autoFocus
      />

      <InputController
        label={content.labelPassword}
        control={control}
        name="password"
        type="password"
        autoComplete="new-password"
        error={errors.password?.message === 'validationRequired' ? errorMessages.password : undefined}
      />

      <PasswordRules isTouched={!!password} error={errors.password} />

      <AuthLayout.Footer>
        <div className={styles.consents}>
          <FormProvider {...form}>
            <GeneralTermsConsent
              guestConsents={guestConsentsResponse?.data.consents}
              isLoading={areGuestConsentsLoading}
            />
            <FirstPartyConsent guestConsents={guestConsentsResponse?.data.consents} />
          </FormProvider>
        </div>

        <Button size="small" type="submit" isLoading={isLoading}>
          {content.signUp}
        </Button>

        <ContentWithLink
          href={isAppWebView ? undefined : '/auth'}
          onClick={handleLoginClick}
          passHref
          shallow
          contentKey="signInCta"
        />
      </AuthLayout.Footer>
    </Form>
  );
};
export default SignUpForm;
